import { inject } from 'aurelia';
import { IRouter } from '@aurelia/router';
import { EventAccessApi, EventAccessEntity } from '../../apis/event-access/event-access-api';
import { EventApi } from '../../apis/event/event-api';
import { EventEntity } from '../../apis/event/event-entity';
import { UserContext } from '../../contexts/user-context';

@inject(IRouter, EventAccessApi, EventApi, UserContext)
export class ShareEvent {
    public eventId: string = '';
    public event: EventEntity | null = null;
    public grants: EventAccessEntity[] = [];
    public newEmail: string = '';
    public isBusy: boolean = false;
    public errorMessage: string | null = null;
    public successMessage: string | null = null;

    public isSendingInvitation: boolean = false;
    public invitationMessage: string | null = null;
    public isSendingRegistration: boolean = false;
    public registrationMessage: string | null = null;

    constructor(
        private readonly _router: IRouter,
        private readonly _eventAccessApi: EventAccessApi,
        private readonly _eventApi: EventApi,
        private readonly _userContext: UserContext,
    ) {}

    public async loading(params: Record<string, string>): Promise<void> {
        this.eventId = params?.eventId;
        await Promise.all([this.loadEvent(), this.loadGrants()]);
    }

    public get isOwner(): boolean {
        return this.event?.userId != null && this.event.userId === this._userContext.userEntity?.id;
    }

    public get showRegistrationAction(): boolean {
        return this.event?.registrationEnabled ?? false;
    }

    public get shareUrl(): string {
        return this.event?.code ? `${window.location.origin}/${this.event.code}` : '';
    }

    public get shareTitle(): string {
        return this.event?.title || this.event?.organizer || window.location.host;
    }

    private async loadEvent(): Promise<void> {
        try {
            this.event = await this._eventApi.getById(this.eventId);
        } catch { /* ignore */ }
    }

    private async loadGrants(): Promise<void> {
        try {
            this.grants = await this._eventAccessApi.list(this.eventId);
        } catch { /* ignore, e.g. not the owner */ }
    }

    public async addGrant(): Promise<void> {
        if (!this.newEmail) return;
        this.isBusy = true;
        this.errorMessage = null;
        this.successMessage = null;
        try {
            const result = await this._eventAccessApi.grant(this.eventId, this.newEmail);
            if (result.statusCode >= 200 && result.statusCode < 300) {
                this.newEmail = '';
                await this.loadGrants();
            } else {
                this.errorMessage = 'share_add_error';
            }
        } catch {
            this.errorMessage = 'share_add_error';
        } finally {
            this.isBusy = false;
        }
    }

    public async revoke(accessId: string): Promise<void> {
        try {
            await this._eventAccessApi.revoke(this.eventId, accessId);
            await this.loadGrants();
        } catch { /* ignore */ }
    }

    public async sendInvitationEmail(): Promise<void> {
        this.isSendingInvitation = true;
        this.invitationMessage = null;
        try {
            await this._eventAccessApi.sendInvitationEmail(this.eventId);
            this.invitationMessage = 'share_invitation_sent';
        } catch {
            this.invitationMessage = 'share_invitation_error';
        } finally {
            this.isSendingInvitation = false;
        }
    }

    public async sendRegistrationEmail(): Promise<void> {
        this.isSendingRegistration = true;
        this.registrationMessage = null;
        try {
            await this._eventAccessApi.sendRegistrationEmail(this.eventId);
            this.registrationMessage = 'share_registration_sent';
        } catch {
            this.registrationMessage = 'share_registration_error';
        } finally {
            this.isSendingRegistration = false;
        }
    }

    public back(): void {
        void this._router.load('my-events');
    }
}
